/**
 * Phần tính của thẻ "Cơ cấu" kiểu thẻ đội (CEO 1a · 2a · 3a · 4a · 5a, 31/10/2026):
 * gom CSKH chung lên đầu đội, ngày chung để ẩn dòng "(từ …)" lặp, màu cố định
 * cho từng đội. Thuần dữ liệu — màn chỉ vẽ.
 */
import type { SalesMember, SalesUnit } from "./api";

export function activeOn(start: string, end: string, day: string): boolean {
  return start <= day && (end === "" || end >= day);
}

/**
 * CSKH đang làm ở MỌI tiểu đội của đội với cùng ngày vào – ngày ra ⇒ hiện một lần
 * ở đầu đội ("CSKH chung"). Ai chỉ theo một phần tiểu đội, hay ngày lệch giữa các
 * tiểu đội, thì vẫn nằm trong từng tiểu đội — gom vào là giấu mất chỗ khác nhau.
 */
export function sharedCskh(
  squadIds: string[],
  members: SalesMember[],
  today: string,
): { shared: SalesMember[]; sharedIds: Set<string> } {
  const sharedIds = new Set<string>();
  const shared: SalesMember[] = [];
  if (squadIds.length === 0) return { shared, sharedIds };
  const bySquad = new Map<string, SalesMember[]>();
  for (const m of members) {
    if (m.kind !== "cskh" || !activeOn(m.startDate, m.endDate, today)) continue;
    if (!squadIds.includes(m.squadId)) continue;
    const list = bySquad.get(m.squadId) ?? [];
    list.push(m);
    bySquad.set(m.squadId, list);
  }
  for (const first of bySquad.get(squadIds[0]!) ?? []) {
    const same = squadIds.map((sid) =>
      (bySquad.get(sid) ?? []).find(
        (m) => m.staffId === first.staffId && m.startDate === first.startDate && m.endDate === first.endDate,
      ),
    );
    if (same.every(Boolean)) {
      shared.push(first);
      for (const m of same) sharedIds.add(m!.id);
    }
  }
  shared.sort((a, b) => a.staffName.localeCompare(b.staffName, "vi"));
  return { shared, sharedIds };
}

/** Ngày vào gặp nhiều nhất (hoà thì ngày sớm hơn) — chỉ người lệch ngày này mới hiện "(từ …)". */
export function commonStart(dates: string[]): string {
  const count = new Map<string, number>();
  for (const d of dates) if (d) count.set(d, (count.get(d) ?? 0) + 1);
  let best = "";
  let bestN = 0;
  for (const [d, n] of count) {
    if (n > bestN || (n === bestN && d < best)) {
      best = d;
      bestN = n;
    }
  }
  return best;
}

/** Màu vạch đội — đủ đậm để thấy trên nền trắng và nền kem, mỗi màu một tông khác hẳn nhau. */
export const TEAM_COLORS = [
  "#6e5090", // tím
  "#357049", // xanh lá
  "#c2571a", // cam
  "#376683", // xanh dương
  "#993556", // hồng
  "#8b6914", // nâu vàng
] as const;

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

/**
 * Mỗi đội một màu cố định theo mã đội. Hai đội trúng cùng màu thì đội lập sau
 * nhích sang màu trống kế tiếp — đội có trước giữ nguyên màu khi thêm đội mới.
 */
export function teamColors(teams: Pick<SalesUnit, "id" | "startDate">[]): Map<string, string> {
  const out = new Map<string, string>();
  const taken = new Set<number>();
  const ordered = [...teams].sort((a, b) => a.startDate.localeCompare(b.startDate) || a.id.localeCompare(b.id));
  for (const t of ordered) {
    let i = hash(t.id) % TEAM_COLORS.length;
    for (let step = 0; step < TEAM_COLORS.length && taken.has(i); step++) i = (i + 1) % TEAM_COLORS.length;
    taken.add(i);
    out.set(t.id, TEAM_COLORS[i]!);
  }
  return out;
}
